"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  FileUpIcon,
  UploadIcon,
} from "lucide-react";
import { useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { VehicleImportResult } from "../api/client";
import {
  useCommitVehicleImportMutation,
  useDownloadVehicleImportTemplateMutation,
  usePreviewVehicleImportMutation,
} from "../api/queries";

const actionLabel: Record<
  VehicleImportResult["rows"][number]["action"],
  string
> = {
  create: "ສ້າງໃໝ່",
  update: "ອັບເດດ",
  skip: "ຂ້າມ",
  error: "ຜິດພາດ",
};

const sheetLabel: Record<
  VehicleImportResult["rows"][number]["sheet"],
  string
> = {
  configurations: "Configurations",
  model_codes: "Model codes",
  code_assignments: "Code assignments",
  chassis_ranges: "Chassis ranges",
};

export function CatalogVehicleImportPage() {
  const nav = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<VehicleImportResult | null>(null);

  const canImport = useActionPermission(["catalog:create"]);
  const downloadTemplate = useDownloadVehicleImportTemplateMutation();
  const previewImport = usePreviewVehicleImportMutation();
  const commitImport = useCommitVehicleImportMutation();

  const handlePreview = async () => {
    if (!selectedFile) return;
    const result = await previewImport.mutateAsync(selectedFile);
    setPreview(result);
  };

  const handleCommit = async () => {
    if (!selectedFile || !preview?.ok) return;
    const result = await commitImport.mutateAsync(selectedFile);
    setPreview(result);
    if (result.ok) {
      nav({ to: "/app/catalog/vehicles" });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ລົດ", to: "/app/catalog/vehicles" },
            { label: "ນຳເຂົ້າ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ນຳເຂົ້າຂໍ້ມູນລົດ (CSV / Excel)
            </h2>
            <p className="text-muted-foreground text-sm">
              ດາວໂຫຼດແມ່ແບບ → ກອກຂໍ້ມູນ → Preview → ຢືນຢັນນຳເຂົ້າ
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/catalog/vehicles" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບ
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. ດາວໂຫຼດແມ່ແບບ</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={downloadTemplate.isPending}
                onClick={() => downloadTemplate.mutate("xlsx")}
              >
                <DownloadIcon className="size-4" />
                Excel (.xlsx)
              </Button>
              <Button
                variant="outline"
                disabled={downloadTemplate.isPending}
                onClick={() => downloadTemplate.mutate("csv")}
              >
                <DownloadIcon className="size-4" />
                CSV (configurations)
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. ອັບໂຫຼດໄຟລ໌</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setSelectedFile(file);
                  setPreview(null);
                }}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon className="size-4" />
                ເລືອກໄຟລ໌
              </Button>
              {selectedFile ? (
                <p className="text-sm">
                  <FileUpIcon className="mr-1 inline size-4" />
                  {selectedFile.name}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ເລືອກໄຟລ໌</p>
              )}
              <Button
                disabled={!selectedFile || previewImport.isPending || !canImport}
                onClick={() => void handlePreview()}
              >
                Preview
              </Button>
            </CardContent>
          </Card>
        </div>

        {preview ? (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base">3. ຜົນ Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant={preview.ok ? "default" : "destructive"}>
                  {preview.ok ? "ພ້ອມນຳເຂົ້າ" : "ມີຂໍ້ຜິດພາດ"}
                </Badge>
                <Badge variant="outline">ສ້າງ {preview.summary.creates}</Badge>
                <Badge variant="outline">ອັບເດດ {preview.summary.updates}</Badge>
                <Badge variant="outline">ຂ້າມ {preview.summary.skips}</Badge>
                <Badge variant="outline">ຜິດພາດ {preview.summary.errors}</Badge>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sheet</TableHead>
                      <TableHead>ແຖວ</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>ລາຍລະອຽດ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((row) => (
                      <TableRow key={`${row.sheet}-${row.rowNumber}-${row.action}`}>
                        <TableCell>{sheetLabel[row.sheet]}</TableCell>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{actionLabel[row.action]}</TableCell>
                        <TableCell className="max-w-lg whitespace-normal">
                          {row.keys?.configurationLabel ??
                            [row.keys?.makeName, row.keys?.modelName]
                              .filter(Boolean)
                              .join(" · ")}
                          {row.message ? ` — ${row.message}` : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Button
                disabled={
                  !preview.ok ||
                  !selectedFile ||
                  commitImport.isPending ||
                  !canImport
                }
                onClick={() => void handleCommit()}
              >
                ຢືນຢັນນຳເຂົ້າ
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </Main>
    </>
  );
}
